'use client';

import { Center, Container, Stack, Text, Title } from '@mantine/core';
import { useRouter } from 'next/navigation';

import { TrackedButton } from '@/components';

import classes from './ErrorPage.module.css';

/**
 * Common HTTP error codes and their default messages
 */
export enum ErrorCode {
  NOT_FOUND = '404',
  UNAUTHORIZED = '401',
  FORBIDDEN = '403',
  SERVER_ERROR = '500',
  BAD_REQUEST = '400',
  SERVICE_UNAVAILABLE = '503',
}

/**
 * Default error messages for common error codes
 */
export const DEFAULT_ERROR_MESSAGES: Record<
  ErrorCode,
  { title: string; message: string }
> = {
  [ErrorCode.NOT_FOUND]: {
    title: 'Nothing to Sway here!',
    message: "We can't seem to find what you're looking for.",
  },
  [ErrorCode.UNAUTHORIZED]: {
    title: 'Authentication Required',
    message: 'Please log in to access this page.',
  },
  [ErrorCode.FORBIDDEN]: {
    title: 'Access Denied',
    message: "You don't have permission to access this resource.",
  },
  [ErrorCode.SERVER_ERROR]: {
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
  },
  [ErrorCode.BAD_REQUEST]: {
    title: 'Invalid Request',
    message: 'The request could not be processed. Please check your input.',
  },
  [ErrorCode.SERVICE_UNAVAILABLE]: {
    title: 'Service Unavailable',
    message: 'This service is temporarily unavailable. Please try again later.',
  },
};

interface ErrorPageProps {
  /**
   * Custom error code
   * @default "404"
   */
  code?: string;

  /**
   * Custom error title
   * @default "Nothing to Sway here!"
   */
  title?: string;

  /**
   * Custom error message
   * @default "We can't seem to find what you're looking for."
   */
  message?: string;

  /**
   * Custom button label
   * @default "HOME"
   */
  buttonLabel?: string;

  /**
   * Where to navigate on button click
   * @default "/"
   */
  redirectPath?: string;
}

export const ErrorPage = ({
  code = ErrorCode.NOT_FOUND,
  title,
  message,
  buttonLabel = 'HOME',
  redirectPath = '/',
}: ErrorPageProps) => {
  const router = useRouter();

  // Use default messages if not provided
  const errorDefaults =
    code in ErrorCode
      ? DEFAULT_ERROR_MESSAGES[code as ErrorCode]
      : DEFAULT_ERROR_MESSAGES[ErrorCode.NOT_FOUND];

  const displayTitle = title || errorDefaults.title;
  const displayMessage = message || errorDefaults.message;

  return (
    <div>
      <Container size="md">
        <Center h="80vh">
          <Stack gap="lg">
            <Title order={2}>
              {code} {displayTitle}
            </Title>
            <Text size="md">{displayMessage}</Text>
            <TrackedButton
              size="lg"
              radius="md"
              className={classes.homeButton}
              onClick={() => router.push(redirectPath)}
              trackingName="error_page_home_button"
              trackingProperties={{
                redirectPath: redirectPath,
              }}
            >
              {buttonLabel}
            </TrackedButton>
          </Stack>
        </Center>
      </Container>
    </div>
  );
};
